import * as THREE from "three";
import type { SuntraceQuality } from "./types";
import type { SuntraceTrack } from "./track";

type SuntraceEnvironmentDecalFamily =
  | "drainage-stain"
  | "salt-streak"
  | "tire-residue";

type SuntraceEnvironmentDecalPlacement = {
  family: SuntraceEnvironmentDecalFamily;
  alignment: "structure" | "track";
  progress: number;
  lateralOffset: number;
  surfaceOffset: number;
  width: number;
  height: number;
};

export const SUNTRACE_ENVIRONMENT_DECAL_CONTRACT = Object.freeze({
  familyCount: 3,
  placementCount: 18,
  maximumDrawCalls: 3,
  maximumTriangles: 36,
  minimumOpacity: 0.42,
  maximumOpacity: 0.62,
});

export const SUNTRACE_WEATHERING_DECAL_PLACEMENTS = Object.freeze([
  { family: "salt-streak", alignment: "structure", progress: 0.083, lateralOffset: -7.48, surfaceOffset: 0.92, width: 1.45, height: 1.08 },
  { family: "salt-streak", alignment: "structure", progress: 0.148, lateralOffset: -7.52, surfaceOffset: 0.78, width: 1.18, height: 0.96 },
  { family: "salt-streak", alignment: "structure", progress: 0.236, lateralOffset: 7.46, surfaceOffset: 0.88, width: 1.62, height: 1.14 },
  { family: "salt-streak", alignment: "structure", progress: 0.407, lateralOffset: -7.5, surfaceOffset: 0.84, width: 1.26, height: 1.02 },
  { family: "salt-streak", alignment: "structure", progress: 0.618, lateralOffset: 7.48, surfaceOffset: 0.76, width: 1.54, height: 0.91 },
  { family: "salt-streak", alignment: "structure", progress: 0.944, lateralOffset: -7.5, surfaceOffset: 0.9, width: 1.34, height: 1.16 },
  { family: "drainage-stain", alignment: "structure", progress: 0.108, lateralOffset: 7.47, surfaceOffset: 0.58, width: 1.02, height: 0.72 },
  { family: "drainage-stain", alignment: "structure", progress: 0.192, lateralOffset: -7.49, surfaceOffset: 0.64, width: 1.28, height: 0.82 },
  { family: "drainage-stain", alignment: "structure", progress: 0.319, lateralOffset: 7.51, surfaceOffset: 0.56, width: 0.94, height: 0.68 },
  { family: "drainage-stain", alignment: "structure", progress: 0.493, lateralOffset: -7.46, surfaceOffset: 0.62, width: 1.36, height: 0.78 },
  { family: "drainage-stain", alignment: "structure", progress: 0.67, lateralOffset: 7.5, surfaceOffset: 0.6, width: 1.12, height: 0.74 },
  { family: "drainage-stain", alignment: "structure", progress: 0.968, lateralOffset: 7.48, surfaceOffset: 0.54, width: 1.24, height: 0.7 },
  { family: "tire-residue", alignment: "track", progress: 0.052, lateralOffset: -1.84, surfaceOffset: 0.012, width: 0.54, height: 2.7 },
  { family: "tire-residue", alignment: "track", progress: 0.275, lateralOffset: 1.42, surfaceOffset: 0.012, width: 0.48, height: 2.25 },
  { family: "tire-residue", alignment: "track", progress: 0.382, lateralOffset: -2.08, surfaceOffset: 0.012, width: 0.58, height: 2.9 },
  { family: "tire-residue", alignment: "track", progress: 0.54, lateralOffset: 1.76, surfaceOffset: 0.012, width: 0.5, height: 2.45 },
  { family: "tire-residue", alignment: "track", progress: 0.705, lateralOffset: -1.28, surfaceOffset: 0.012, width: 0.46, height: 2.1 },
  { family: "tire-residue", alignment: "track", progress: 0.918, lateralOffset: 1.92, surfaceOffset: 0.012, width: 0.56, height: 2.75 },
] as const satisfies readonly SuntraceEnvironmentDecalPlacement[]);

const decalFamilies = [
  { id: "salt-streak", atlasColumn: 0, opacity: 0.54 },
  { id: "drainage-stain", atlasColumn: 1, opacity: 0.46 },
  { id: "tire-residue", atlasColumn: 2, opacity: 0.6 },
] as const satisfies readonly {
  id: SuntraceEnvironmentDecalFamily;
  atlasColumn: number;
  opacity: number;
}[];

const createWeatheringAtlas = (): THREE.DataTexture => {
  const tileSize = 32;
  const width = tileSize * decalFamilies.length;
  const height = tileSize;
  const data = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const tile = Math.floor(x / tileSize);
      const localX = (x % tileSize) / (tileSize - 1);
      const localY = y / (height - 1);
      const centeredX = Math.abs(localX - 0.5) * 2;
      const centeredY = Math.abs(localY - 0.5) * 2;
      const edgeFade = Math.max(0, 1 - Math.max(centeredX, centeredY));
      const grain = 0.72 + Math.sin(x * 2.17 + y * 1.63) * 0.18;
      const salt = Math.max(0, 1 - centeredX * 1.35) * (0.45 + localY * 0.55);
      const drainage = Math.max(0, 1 - centeredX * 1.8) * Math.max(0, 1 - localY * 0.65);
      const tire = Math.max(0, 1 - centeredX * 2.4) * (0.68 + Math.sin(localY * 24) * 0.12);
      const familyMask = tile === 0 ? salt : tile === 1 ? drainage : tire;
      const alpha = Math.round(255 * THREE.MathUtils.clamp(edgeFade * familyMask * grain, 0, 1));
      const offset = (y * width + x) * 4;
      const tone = tile === 0 ? 206 : tile === 1 ? 77 : 48;
      data[offset] = tone;
      data[offset + 1] = tile === 0 ? 214 : tone;
      data[offset + 2] = tile === 0 ? 207 : tile === 1 ? 70 : 43;
      data[offset + 3] = alpha;
    }
  }
  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  texture.name = "SUNTRACE localized weathering atlas";
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
};

const createFamilyGeometry = (
  alignment: SuntraceEnvironmentDecalPlacement["alignment"],
  atlasColumn: number,
): THREE.PlaneGeometry => {
  const geometry = new THREE.PlaneGeometry(1, 1);
  if (alignment === "track") {
    geometry.rotateX(-Math.PI * 0.5);
  } else {
    geometry.rotateY(Math.PI * 0.5);
  }
  const uv = geometry.getAttribute("uv");
  const atlasWidth = decalFamilies.length;
  for (let index = 0; index < uv.count; index += 1) {
    uv.setX(index, (atlasColumn + uv.getX(index)) / atlasWidth);
  }
  uv.needsUpdate = true;
  geometry.computeBoundingSphere();
  return geometry;
};

export type SuntraceEnvironmentDecals = {
  group: THREE.Group;
  setQuality: (quality: SuntraceQuality) => void;
  dispose: () => void;
};

export const createSuntraceEnvironmentDecals = (
  track: SuntraceTrack,
): SuntraceEnvironmentDecals => {
  const group = new THREE.Group();
  group.name = "SUNTRACE localized environment decals";
  const atlas = createWeatheringAtlas();
  const geometries: THREE.BufferGeometry[] = [];
  const materials: THREE.Material[] = [];
  const frame = track.sample(0);
  const placementObject = new THREE.Object3D();

  for (const family of decalFamilies) {
    const placements = SUNTRACE_WEATHERING_DECAL_PLACEMENTS.filter(
      ({ family: placementFamily }) => placementFamily === family.id,
    );
    const alignment = placements[0]?.alignment ?? "structure";
    const geometry = createFamilyGeometry(alignment, family.atlasColumn);
    const material = new THREE.MeshBasicMaterial({
      map: atlas,
      transparent: true,
      opacity: family.opacity,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    const mesh = new THREE.InstancedMesh(geometry, material, placements.length);
    mesh.name = `SUNTRACE ${family.id} decals`;
    for (const [index, placement] of placements.entries()) {
      if (placement.alignment === "track") {
        track.sample(placement.progress, placement.lateralOffset, frame);
      } else {
        track.samplePlacement(placement.progress, placement.lateralOffset, frame);
      }
      placementObject.position
        .copy(frame.position)
        .addScaledVector(frame.normal, placement.surfaceOffset);
      placementObject.quaternion.copy(frame.quaternion);
      if (placement.alignment === "track") {
        placementObject.scale.set(placement.width, 1, placement.height);
      } else {
        placementObject.scale.set(1, placement.height, placement.width);
      }
      placementObject.updateMatrix();
      mesh.setMatrixAt(index, placementObject.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
    geometries.push(geometry);
    materials.push(material);
    group.add(mesh);
  }

  const setQuality = (quality: SuntraceQuality): void => {
    group.visible = quality === "high";
  };
  setQuality("high");
  return {
    group,
    setQuality,
    dispose() {
      group.removeFromParent();
      group.clear();
      for (const geometry of geometries) {
        geometry.dispose();
      }
      for (const material of materials) {
        material.dispose();
      }
      atlas.dispose();
    },
  };
};
